/**
 * Configuration for state-based business operations and marketing content.
 *
 * STATES_SELLING_INSURANCE: States where we actively sell insurance
 * STATES_WITH_MARKETING_PAGES: States where marketing landing pages are accessible
 */

/**
 * States where Guardian Service actively sells insurance.
 *
 * Use this for:
 * - Routing users to /compare (form available) vs /join-waitlist
 * - ZIP code validation in forms
 * - Determining if a user's state has active insurance operations
 *
 */
export const STATES_SELLING_INSURANCE: string[] = [
  "Alabama",
  "Arizona",
  "Colorado",
  "Connecticut",
  "Georgia",
  "Indiana",
  "Kentucky",
  "Maryland",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Nevada",
  "New Mexico",
  "North Carolina",
  "Ohio",
  "Oregon",
  "South Carolina",
  "Tennessee",
  "Texas",
  "Utah",
  "Virginia",
  "Washington",
];

/**
 * States with "You're Invited" landing pages (/invite-{abbr}).
 * Kept in sync with current invite rollout; add states here when new invite pages ship.
 */
export const STATES_WITH_INVITE_PAGES: string[] = [...STATES_SELLING_INSURANCE];

/**
 * States with Call Forward landing pages (`/gc-{abbr}` and `/gc2-{abbr}`).
 */
export const STATES_WITH_CALL_FORWARD_PAGES: string[] = [
  "Arizona",
  "Georgia",
  "North Carolina",
  "Tennessee",
  "Virginia",
  "Washington",
];

/**
 * States where marketing landing pages are accessible.
 *
 * Use this for:
 * - Validating URL parameters for /opay/[st], /gnews/[st], /gc/[st], and /gc2/[st] routes
 * - Determining which state-specific landing pages render (vs 404)
 *
 * Note: May include states where we don't yet sell insurance (pre-launch marketing).
 * Typically a superset of STATES_SELLING_INSURANCE.
 *
 */
export const STATES_WITH_MARKETING_PAGES: string[] = [
  "Alabama",
  "Arizona",
  "Colorado",
  "Connecticut",
  "Georgia",
  "Indiana",
  "Kentucky",
  "Maryland",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Nevada",
  "New Jersey",
  "New Mexico",
  "North Carolina",
  "Ohio",
  "Oregon",
  "Pennsylvania",
  "South Carolina",
  "Tennessee",
  "Texas",
  "Utah",
  "Virginia",
  "Washington",
];

// States we plan to launch in soon, but are not doing business in yet.
export const COMING_SOON_STATES: string[] = [
  "Florida",
  "Louisiana",
  "Arkansas",
  "Oklahoma",
  "Pennsylvania",
  "Massachusetts",
];

// Abbreviations of states whose `/about/states/{abbr}` page is temporarily
// disabled. In-app links (StatesList, MapSection) are suppressed and the
// page itself 404s — primarily because per-state image/copy maps in
// `statesInfo.ts` do not yet have entries for these states, so prerendering
// would fail with missing GcsImage dimensions.
export const STATE_PAGE_LINKS_DISABLED: string[] = [];

// Map of state full names to abbreviations
export const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

// Reverse map for abbreviation to full name lookup
export const STATE_NAMES: Record<string, string> = Object.entries(
  STATE_ABBREVIATIONS,
).reduce(
  (acc, [fullName, abbr]) => {
    acc[abbr] = fullName;
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Check if Guardian Service actively sells insurance in this state.
 *
 * @param stateAbbr - Two-letter state abbreviation (e.g., "NC")
 * @returns true if we sell insurance in this state
 *
 * @example
 * if (isSellingInsuranceInState("NC")) {
 *   router.push("/compare");
 * } else {
 *   router.push("/join-waitlist");
 * }
 */
export function isSellingInsuranceInState(stateAbbr: string): boolean {
  if (!stateAbbr) return false;
  const fullName = STATE_NAMES[stateAbbr.toUpperCase()];
  return STATES_SELLING_INSURANCE.includes(fullName);
}

/**
 * Check if a state has marketing landing pages available.
 *
 * @param stateAbbr - Two-letter state abbreviation (e.g., "NC")
 * @returns true if marketing pages are accessible for this state
 *
 * @example
 * if (hasMarketingPage("NC")) {
 *   return <StateLandingPage state="NC" />;
 * } else {
 *   notFound();
 * }
 */
export function hasMarketingPage(stateAbbr: string): boolean {
  if (!stateAbbr) return false;
  const fullName = STATE_NAMES[stateAbbr.toUpperCase()];
  return STATES_WITH_MARKETING_PAGES.includes(fullName);
}

/**
 * Whether `/invite-{abbr}` is defined for this state (rewrites to /gs-route/invite/{abbr}).
 */
export function hasInvitePage(stateAbbr: string): boolean {
  if (!stateAbbr) return false;
  const fullName = STATE_NAMES[stateAbbr.toUpperCase()];
  return STATES_WITH_INVITE_PAGES.includes(fullName);
}

/**
 * Whether `/gc-{abbr}` and `/gc2-{abbr}` are defined for this state.
 */
export function hasCallForwardPage(stateAbbr: string): boolean {
  if (!stateAbbr) return false;
  const fullName = STATE_NAMES[stateAbbr.toUpperCase()];
  return STATES_WITH_CALL_FORWARD_PAGES.includes(fullName);
}

// id of topojson (of us-atlas) to abbreviation for easier lookup when rendering map
export const TOPO_ID_TO_ABBR: Record<string, string> = {
  "01": "AL",
  "02": "AK",
  "04": "AZ",
  "05": "AR",
  "06": "CA",
  "08": "CO",
  "09": "CT",
  "10": "DE",
  "12": "FL",
  "13": "GA",
  "15": "HI",
  "16": "ID",
  "17": "IL",
  "18": "IN",
  "19": "IA",
  "20": "KS",
  "21": "KY",
  "22": "LA",
  "23": "ME",
  "24": "MD",
  "25": "MA",
  "26": "MI",
  "27": "MN",
  "28": "MS",
  "29": "MO",
  "30": "MT",
  "31": "NE",
  "32": "NV",
  "33": "NH",
  "34": "NJ",
  "35": "NM",
  "36": "NY",
  "37": "NC",
  "38": "ND",
  "39": "OH",
  "40": "OK",
  "41": "OR",
  "42": "PA",
  "44": "RI",
  "45": "SC",
  "46": "SD",
  "47": "TN",
  "48": "TX",
  "49": "UT",
  "50": "VT",
  "51": "VA",
  "53": "WA",
  "54": "WV",
  "55": "WI",
  "56": "WY",
};

//Add agents location
export const agentLocations = [
  { city: "Simi Valley", state: "CA" },
  { city: "Raleigh", state: "NC" },
  { city: "Fremont", state: "NC" },
  { city: "Phoenix", state: "AZ" },
  { city: "Madison", state: "WI" },
  { city: "Eau Claire", state: "WI" },
  { city: "Minneapolis", state: "MN" },
  { city: "Jacksonville", state: "FL" },
  { city: "Chicago", state: "IL" },
  { city: "St. Augustine", state: "FL" },
  { city: "Seattle", state: "WA" },
  { city: "Pueblo", state: "CO" },
  { city: "Bay Village", state: "OH" },
  { city: "Miami", state: "FL" },
  { city: "Pinehurst", state: "NC" },
  { city: "Jarrell", state: "TX" },
  { city: "Camarillo", state: "CA" },
  { city: "Austin", state: "TX" },
  { city: "Atlanta", state: "GA" },
  { city: "Fort Lee", state: "NJ" },
  { city: "Charlotte", state: "NC" },
  { city: "Lake Mary", state: "FL" },
  { city: "Ewing", state: "NJ" },
  { city: "Kansas City", state: "MO" },
  { city: "Dayton", state: "OH" },
  { city: "Spokane", state: "WA" },
  { city: "Sarasota", state: "FL" },
  { city: "Los Angeles", state: "CA" },
  { city: "Las Vegas", state: "NV" },
  { city: "South Holland", state: "IL" },
  { city: "Boynton", state: "FL" },
  { city: "San Antonio", state: "TX" },
  { city: "Tucson", state: "AZ" },
  { city: "Corona", state: "CA" },
  { city: "New York", state: "NY" },
  { city: "Cleveland", state: "OH" },
  { city: "Chandler", state: "AZ" },
  { city: "Columbia", state: "SC" },
  { city: "New York", state: "NY" },
  { city: "The Woodlands", state: "TX" },
  { city: "Columbus", state: "OH" },
  { city: "Hutto", state: "TX" },
  { city: "Henderson", state: "NV" },
  { city: "New Philadelphia", state: "OH" },
  { city: "Midlothian", state: "TX" },
  { city: "Bealeton", state: "VA" },
  { city: "Summerville", state: "SC" },
  { city: "Torrance", state: "CA" },
  { city: "Painesville", state: "OH" },
  { city: "Chesterland", state: "OH" },
  { city: "Westlake", state: "OH" },
  { city: "Sierra Vista", state: "AZ" },
  { city: "Wilmington", state: "NC" },
  { city: "Seguin", state: "TX" },
  { city: "Westerville", state: "OH" },
  { city: "Pflugerville", state: "TX" },
  { city: "Westport", state: "MA" },
  { city: "Huntington", state: "NY" },
  { city: "Havertown", state: "PA" },
  { city: "Holly Springs", state: "NC" },
  { city: "Ruther Glen", state: "VA" },
];
